"use client";
import { type ElementType, forwardRef, useState } from "react";
import Typography from "@/shared/components/common/Typography";
import data from "@/public/data/hotel/hotel.roomCounter.json";
import Counter from "@/shared/components/common/counter";
import { PlusCircle, MinusCircle, XCircle } from "@phosphor-icons/react";
import Container from "@/shared/components/common/Container";
import Separator from "@/shared/components/common/separator";
import { Button } from "@/shared/components/common/button";
import AgeSelect from "./AgeSelect";
import Flex from "@/shared/components/common/Flex";
import { isUndefined } from "@/shared/libs/type-utils";

type TRoomDetails = { adult: number; child: string[] }[];
interface IHotelRoomCounter {
  className?: string;
  onClick: (input: TRoomDetails) => void;
  roomData: { adult: number; child: string[] }[];
  OnCloseComp: ElementType;
  service?: "hotel" | "tour";
}

const RoomPicker = forwardRef<HTMLButtonElement, IHotelRoomCounter>(
  (
    { className, OnCloseComp, roomData, service = "hotel", onClick, ...props },
    ref,
  ) => {
    const { options, unit, counterList, subTitle, buttons, passenger } = data;

    const defaultRoomOccupancy = { adult: 1, child: [] };
    const [roomDetails, setRoomDetails] = useState<TRoomDetails>(
      roomData ?? [defaultRoomOccupancy],
    );

    const handleNumberOfRooms = () => {
      setRoomDetails((pre) =>
        pre.length < 4 ? [...pre, defaultRoomOccupancy] : pre,
      );
    };

    const handleCounters =
      (index: number, value: Omit<IPerson, "infant">) => (count: number) => {
        setRoomDetails((pre) =>
          pre.map((room, i) =>
            i === index
              ? {
                  ...room,
                  [value as IPerson]:
                    value === "adult" ? count : Array(count).fill(""),
                }
              : room,
          ),
        );
      };

    const handleCountPersonInRooms = () =>
      roomDetails.reduce(
        (pre, cur) => pre + cur.adult + (cur.child ? cur.child.length : 0),
        0,
      );

    const handleAgeOfChild =
      (roomNumber: number, childOrder: number) => (age: string) => {
        setRoomDetails((pre) =>
          pre.map((room, i) =>
            i === roomNumber
              ? {
                  ...room,
                  ...(room.child && {
                    child: room.child.map((itm, _i) =>
                      _i === childOrder ? age.toString() : itm,
                    ),
                  }),
                }
              : room,
          ),
        );
      };

    const handleRemoveRoom = (roomNumber: number) => () => {
      setRoomDetails((pre) => pre.filter((room, i) => i !== roomNumber));
    };

    const handleOnClick = (rooms: TRoomDetails) => () => {
      onClick(rooms);
    };

    return (
      <Flex className="px-2 flex-col">
        <Flex className="max-h-[280px] py-2 flex-col overflow-y-auto">
          {Array(roomDetails.length)
            .fill("")
            .map((_, i) => (
              <Flex className="w-full flex-col px-2 sm:px-0" key={i}>
                <Flex className="justify-between items-center px-2">
                  <Typography className="font-semibold">
                    {subTitle} {counterList[i]}
                  </Typography>
                  {roomDetails.length > 1 && (
                    <Button
                      variant={"ghost"}
                      className="mt-3"
                      onClick={handleRemoveRoom(i)}
                    >
                      <XCircle className="text-2xl text-ghost" />
                    </Button>
                  )}
                </Flex>
                <Container>
                  {(service === "hotel" ? [options[0]] : options).map(
                    ({ title, desc, value }, _) => (
                      <Flex className="my-6 flex flex-nowrap" key={title}>
                        <Flex className="flex-col">
                          <Typography className="font-medium">
                            {title}
                          </Typography>
                          <Typography className="text-xs font-normal">
                            {desc}
                          </Typography>
                        </Flex>
                        <Flex className="flex-col">
                          <Counter
                            name={value}
                            onCount={handleCounters(i, value)}
                            format={{ suffix: unit }}
                            classNames={{
                              counterText: "min-w-fit",
                              parent: "flex-nowrap gap-x-3",
                            }}
                            initial={
                              value !== "child"
                                ? roomDetails[i][value as "adult"]
                                : roomDetails[i][value as "child"].length
                            }
                            buttonPlaceHolders={{
                              icons: {
                                increase: (
                                  <PlusCircle className="ring-0! rounded-none! w-7 h-7" />
                                ),
                                decrease: (
                                  <MinusCircle className="ring-0! rounded-none! w-7 h-7" />
                                ),
                              },
                            }}
                          />
                        </Flex>
                      </Flex>
                    ),
                  )}
                </Container>
                <Flex className="justify-start gap-2">
                  {!isUndefined(roomDetails[i].child) &&
                  roomDetails[i]?.child.length > 0
                    ? roomDetails[i]?.child.map((itm, index) => (
                        <Flex className="flex-col" key={index}>
                          <AgeSelect
                            onClick={handleAgeOfChild(i, index)}
                            selectedAge={String(roomDetails[i]?.child[index])}
                          />
                        </Flex>
                      ))
                    : null}
                </Flex>
              </Flex>
            ))}
        </Flex>

        <Flex className="flex-col">
          <Separator direction="horizontal" />
          <Flex className="w-full justify-between py-2 pt-3">
            <Button
              variant="outline"
              disabled={roomDetails.length === 4}
              onClick={handleNumberOfRooms}
            >
              {buttons.addRoom}
            </Button>

            <OnCloseComp>
              <Button
                className="w-full min-w-fit rounded-xs"
                onClick={handleOnClick(roomDetails)}
              >
                {`${buttons.confirmed} - ${
                  roomDetails.length
                } ${subTitle}/${handleCountPersonInRooms()} ${passenger}`}
              </Button>
            </OnCloseComp>
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

RoomPicker.displayName = "RoomCounter";
export default RoomPicker;
